<template>
  <CForm @submit="submit">
    <CModal :title="$t('requestExecutionModal.title')" @close="close">
      <template #body>
        <CCard v-if="isAdmin" class="u-mb-5">
          <CLabel>{{ $t('approveExecutionModal.job') }}</CLabel>
          <h2>{{ job?.getTitle() }}</h2>
        </CCard>

        <CField type="radio" v-model="formData.status" :options="typeOptions" required
                :label="$t('approveExecutionModal.type')" #option="{option}">
          <CItem :icon="getTypeIcon(option)" :title="getTypeTitle(option)" :body="getTypeBody(option)"/>
        </CField>

        <template v-if="isAdmin">
          <CField type="radiobar" v-model="formData.agree" :options="[true, false]" :label="$t('approveExecutionModal.approveLabel')" #option="{option}">
            <CFlex>
              <CColor :theme="option ? 'success' : 'danger'">
                <CIcon :name="option ? 'thumbs-up' : 'thumbs-down'"/>
              </CColor>
              <p>{{ $t(`approveExecutionModal.${option ? 'approve' : 'disapprove'}`) }}</p>
            </CFlex>
          </CField>
        </template>

        <CField v-else type="checklist" v-model="formData.jobs" :options="context.jobs" :label="$t('requestExecutionModal.selectJob')" selectAll required>
          <template #option="{ option: job}">
            <CJob :job/>
          </template>
        </CField>

        <CField v-if="isAdmin" v-model="formData.duration" type="duration" :label="getDurationLabel()" :placeholder="getDurationLabel()" :config="durationConfig" icon="stopwatch"/>
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CModal from '@/general/components/CModal.vue';
  import AuthService from '@/general/services/AuthService';
  import CCard from '@/shared/components/CCard.vue';
  import CColor from '@/shared/components/CColor.vue';
  import CFlex from '@/shared/components/CFlex.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CItem from '@/shared/components/CItem.vue';
  import CLabel from '@/shared/components/form/CLabel.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import Permission from '@/shared/constants/Permission';
  import CJob from '../components/CJob.vue';
  import ExecutionStatus from '../constants/ExecutionStatus';
  import Job, { UpdateExecutionStatusData } from '../models/Job';
  import JobContext from '../models/JobContext';
  import JobService from '../services/JobService';

  const EXECUTE_STATUSES = [ExecutionStatus.EXECUTE_DIAGNOSIS, ExecutionStatus.EXECUTE];
  const REQUEST_STATUSES = [ExecutionStatus.REQUESTED_DIAGNOSIS, ExecutionStatus.REQUESTED_EXECUTION];
  const DIAGNOSIS_STATUSES = [ExecutionStatus.REQUESTED_DIAGNOSIS, ExecutionStatus.EXECUTE_DIAGNOSIS];

  const props = defineProps<{
    job: Job;
    context: JobContext;
  }>();

  const emit = defineEmits<{ (e: 'close') }>();

  const i18n = useI18n();

  const isAdmin = computed(() => AuthService.getCurrentUser().hasPermission(Permission.CAN_APPROVE_EXECUTION));

  const formData = reactive({
    status: getDefaultStatus(),
    agree: true,
    duration: null,
    jobs: getDefaultJobs(),
  }) as UpdateExecutionStatusData;

  const durationConfig = {
    startTime: {
      minutes: 0,
      hours: 1,
    },
  };

  const typeOptions = computed(() => isAdmin.value ? EXECUTE_STATUSES : REQUEST_STATUSES);

  function getDefaultStatus(): ExecutionStatus | null {
    if (isAdmin.value && EXECUTE_STATUSES.includes(props.job.executionStatus)) {
      return props.job.executionStatus;
    } else if (!isAdmin.value) {
      return ExecutionStatus.REQUESTED_EXECUTION;
    } else {
      return null;
    }
  }

  function getDefaultJobs(): Array<Job> {
    if (isAdmin.value) {
      return [props.job];
    } else {
      return props.context.jobs.filter(j => j.executionStatus === ExecutionStatus.NOT_GIVEN || j === props.job);
    }
  }

  function getTypeIcon(option: ExecutionStatus) {
    return DIAGNOSIS_STATUSES.includes(option) ? 'search-eye' : 'wrench';
  }

  function getTypeTitle(option: ExecutionStatus) {
    if (DIAGNOSIS_STATUSES.includes(option)) {
      return i18n.t('requestExecutionModal.executeDiagnosisTitle');
    } else {
      return i18n.t('requestExecutionModal.executeJobTitle');
    }
  }

  function getTypeBody(option: ExecutionStatus) {
    switch (option) {
      case ExecutionStatus.REQUESTED_DIAGNOSIS:
        return i18n.t('requestExecutionModal.requestDiagnosisDescription');
      case ExecutionStatus.REQUESTED_EXECUTION:
        return i18n.t('requestExecutionModal.requestJobExecution');
      case ExecutionStatus.EXECUTE_DIAGNOSIS:
        return i18n.t('approveExecutionModal.executeDiagnosisDescription');
      case ExecutionStatus.EXECUTE:
        return i18n.t('approveExecutionModal.executeJobDescription');
    }
  }

  function getDurationLabel() {
    if (formData.status === ExecutionStatus.EXECUTE) {
      return i18n.t('approveExecutionModal.givenExecutionDuration');
    } else {
      return i18n.t('approveExecutionModal.givenDiagnosisDuration');
    }
  }

  async function submit() {
    await JobService.updateExecutionStatus(formData, isAdmin.value, props.context);
    close();
  }

  function close() {
    emit('close');
  }
</script>